<template>
  <layout-dashboard>
    <h5 class="font-weight-bold m-0">
      Manage Security Group
    </h5>
    <small class="text-info">
      Manage User
    </small>

    <div class="row mt-3">
      <div class="col-12 col-md-4 mb-3">
        <div class="card">
          <div class="card-body px-0 py-4">
            <div class="d-flex align-items-center justify-content-between flex-wrap px-4">
              <div class="font-weight-bold mb-2 mr-2">
                Security Group List
              </div>
              <button
                v-if="permissionAccess.permissions.includes('add')"
                class="btn btn-sm btn-info mb-2"
                @click="createRole"
              >
                {{ $t('manage-security-group.create-group') }}
              </button>
            </div>
            <div class="mt-2">
              <a
                v-for="(role, r) of roles"
                :key="r"
                class="d-flex text-decoration-none px-4 py-2"
                :class="{
                  'text-dark': selectedRole.id !== role.id,
                  'bg-info text-white': selectedRole.id === role.id
                }"
                @click="selectRole(role)"
              >
                <div>{{ role.name }}</div>
                <a
                  v-if="permissionAccess.permissions.includes('delete')"
                  class="text-danger ml-auto"
                  @click.stop="confirmDelete(role)"
                >
                  <i
                    v-if="!role.isReserved"
                    class="fa fa-trash"
                  />
                </a>
              </a>
            </div>
          </div>
        </div>
      </div>

      <div class="col-12 col-md-8 mb-3">
        <div class="card">
          <div class="card-body">
            <create-security-group
              v-if="newRole"
              :pre-columns="preColumns"
              :permission-types="permissionTypes"
              :role="newRole"
              :error="newRoleError"
              @save="save"
            />

            <edit-security-group
              v-else-if="!newRole && Object.keys(selectedRole).length && permissionAccess.permissions.includes('read')"
              :pre-columns="preColumns"
              :permission-types="permissionTypes"
              :permission-access="permissionAccess"
              :role="selectedRole"
              @save="update"
            />

            <h6
              v-else
              class="my-5 text-center text-muted"
            >
              {{ $t('manage-security-group.select-a-group') }}
            </h6>
          </div>
        </div>
      </div>
    </div>

    <modal
      :shown="success.modal"
      @close="resetSuccess"
    >
      <div class="p-5 text-center">
        <div>{{ success.text }}</div>
        <button
          class="btn btn-outline-info btn-sm mt-3"
          @click="resetSuccess"
        >
          OK
        </button>
      </div>
    </modal>

    <modal
      :shown="del.modal"
      @close="resetDelete"
    >
      <div class="p-5 text-center">
        <div>{{ $t('manage-security-group.confirm-delete', { name: del.role.name }) }}</div>
        <button
          class="btn btn-link text-decoration-none btn-sm mt-3"
          @click="resetDelete"
        >
          {{ $t('manage-security-group.cancel') }}
        </button>
        <button
          class="btn btn-danger btn-sm mt-3"
          @click="deleteRole"
        >
          {{ $t('manage-security-group.delete') }}
        </button>
      </div>
    </modal>
  </layout-dashboard>
</template>

<script>
import api from '@/utils/Api'
import LayoutDashboard from '@/components/LayoutDashboard'
import Modal from '@/components/Modal'
import CreateSecurityGroup from './ManageSecurityGroup/CreateSecurityGroup'
import EditSecurityGroup from './ManageSecurityGroup/EditSecurityGroup'

export default {
  components: {
    LayoutDashboard,
    Modal,
    CreateSecurityGroup,
    EditSecurityGroup
  },

  data: () => ({
    permissions: [],
    permissionAccess: [],
    roles: [],
    selectedRole: {},
    selectedRoleChanged: false,
    newRole: null,
    newRoleError: {},
    success: {
      modal: false,
      text: ''
    },
    del: {
      modal: false,
      role: {}
    }
  }),

  computed: {
    preColumns: () => ([
      { name: 'check all', align: 'center' },
      { name: 'label' }
    ]),
    permissionTypes: () => ([
      { name: 'browse', align: 'center' },
      { name: 'read', align: 'center' },
      { name: 'edit', align: 'center' },
      { name: 'add', align: 'center' },
      { name: 'delete', align: 'center' }
    ])
  },

  watch: {
    selectedRole: {
      deep: true,
      handler (val, oldVal) {
        if (!oldVal || (oldVal.id !== val.id)) {
          this.selectedRoleChanged = false
        } else {
          this.selectedRoleChanged = true
        }
      }
    }
  },

  created () {
    this.getPermissionAccess()
    this.fetchPermissions()
    this.fetch()
  },

  methods: {
    getPermissionAccess () {
      const dataLoc = JSON.parse(localStorage.getItem('profile'))
      const index = dataLoc.role.permissions.findIndex(element => element.category === 'role')
      this.permissionAccess = dataLoc.role.permissions[index]
    },
    async fetchPermissions () {
      const { data } = await api.masterData.permissions()
      this.permissions = data.data
    },
    async fetch () {
      const { data } = await api.roles.list()
      this.roles = data
    },
    async save () {
      try {
        await api.roles.save({
          ...this.newRole,
          permissions: this.newRole.permissions
            .map(({ category, permissions }) => ({
              category,
              types: permissions
            }))
        })
        this.success = {
          modal: true,
          text: this.$t('manage-security-group.success-create', { name: this.newRole.name })
        }
        await this.fetch()
      } catch ({ response }) {
        this.newRoleError = response.data.message
      }
    },
    async update () {
      await api.roles.update(
        this.selectedRole.id,
        {
          name: this.selectedRole.name,
          permissions: this.selectedRole.permissions
            .map(({ category, permissions }) => ({
              category,
              types: permissions
            }))
        }
      )
      this.success = {
        modal: true,
        text: this.$t('manage-security-group.success-update', { name: this.selectedRole.name })
      }
      await this.fetch()
      this.selectedRoleChanged = false
    },
    async deleteRole () {
      await api.roles.delete(this.del.role.id)
      this.del.modal = false
      this.success = {
        modal: true,
        text: this.$t('manage-security-group.success-delete', { name: this.del.role.name })
      }
      await this.fetch()
      this.resetDelete()
    },
    selectRole (role) {
      this.newRole = null
      this.selectedRole = {
        ...role,
        permissions: [
          ...role.permissions,
          ...this.permissions
            .filter(({ category }) => !role.permissions
              .map(({ category }) => category)
              .includes(category)
            )
            .map(({ category, name }) => ({
              category,
              name,
              permissions: []
            }))
        ]
      }
    },
    createRole () {
      this.newRole = {
        name: '',
        permissions: this.permissions.map(({ category, name }) => ({
          category,
          name,
          permissions: []
        }))
      }
      this.selectedRole = {}
    },
    resetSuccess () {
      this.newRole = null
      this.success = {
        modal: false,
        name: ''
      }
    },
    confirmDelete (role) {
      this.del = {
        modal: true,
        role: role
      }
    },
    resetDelete () {
      this.del = {
        modal: false,
        role: {}
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.card {
  border: 0;
  border-radius: 18px;
  font-size: 12px;
  min-height: 433px;
}

.table-striped tbody tr:nth-of-type(odd) {
  background: #F5F6FE;
}
</style>
